import React from 'react';
import { FilteredCongestionReportsOverviewProps } from 'components/CongestionReportsOverview';
import {
  Box,
  GridItem,
  Heading,
  JumpToMainContent,
  PageGrid,
  RichText,
  ScrollLinkWrapper,
  Stack,
} from '@ads-core/components';
import { useParams } from 'src/hooks/useParams';
import { useQuery } from '@tanstack/react-query';
import { IntegrationApi } from '@alliander-fe/api';

import { Alerts } from '../Filters/Alerts';
import { Overview } from './_Overview';
import { FilterContainer } from './_FilterContainer';

export const CongestionReportsOverview = ({
  title,
  text,
  detailWildcardPage,
  ...props
}: FilteredCongestionReportsOverviewProps) => {
  const {
    page,
    province,
    status,
    congestion,
    postalCode,
    houseNumber,
    houseNumberAddition,
    startDate,
    endDate,
  } = useParams();

  // Handles the pagination:
  const limit = 10;
  const currentPage = Number(page) || 1;

  // Request the service and availability when we have a postal code and house number
  // So we can inform the user the address is within the service area.
  const { data: addressData, isLoading: isAddressLoading } = useQuery({
    enabled: Boolean(postalCode && houseNumber),
    queryKey: ['addressData', postalCode, houseNumber],
    queryFn: () =>
      IntegrationApi.serviceAvailabilityEndpointsGetServiceAvailability({
        postalCode: postalCode,
        houseNumber: Number(houseNumber),
        addition: houseNumberAddition || '',
      }),
  });

  // Request the congestion reports
  const {
    isLoading: isCongestionReportsLoading,
    data: congestionReportsData,
    status: congestionReportsStatus,
  } = useQuery({
    queryKey: [
      'congestionReports',
      postalCode,
      currentPage,
      province,
      congestion,
      status,
      startDate,
      endDate,
    ],
    queryFn: () =>
      IntegrationApi.congestionReportsEndpointsGetCongestionReports({
        Amount: limit,
        Offset: (currentPage - 1) * limit,
        PostalCode: postalCode,
        Province: province,
        CongestionType: congestion,
        Status: status,
        AnnouncementDateFrom: startDate,
        AnnouncementDateTo: endDate,
      }),
  });

  const totalAmount = congestionReportsData?.totalAmount ?? 0;
  const amountOfPages = Math.ceil(totalAmount / limit);
  const isOperational = addressData?.isOperational;
  const isLoading = isAddressLoading || isCongestionReportsLoading;
  const pageItems = congestionReportsData?.congestionReports;

  return (
    <>
      <JumpToMainContent
        id="zoek-resultaten"
        anchor="#resultaten-vooraankondigingen"
        title="Ga naar zoek resultaten"
      />
      <ScrollLinkWrapper anchorId="overzicht-congestie-onderzoeken">
        <PageGrid>
          <Stack gap={4} asChild>
            <Box paddingBottom={4} asChild>
              <GridItem>
                <Heading size="h2">{title.toString()}</Heading>
                <RichText>{text}</RichText>
              </GridItem>
            </Box>
          </Stack>
          <GridItem columnStart="1" columnEnd={{ initial: '-1', lg: '4' }}>
            <FilterContainer
              searchLabel={props.searchLabel?.toString()}
              dateFilterLabel={props.dateFilterLabel?.toString()}
              provinceLabel={props.provinceLabel?.toString()}
              congestionTypeLabel={props.congestionTypeLabel?.toString()}
              statusLabel={props.statusLabel?.toString()}
            />
          </GridItem>
          <GridItem columnStart={{ initial: '1', lg: '4' }} columnEnd="-1">
            <ScrollLinkWrapper anchorId="resultaten-vooraankondigingen">
              <Box width="100%">
                <Alerts
                  status={congestionReportsStatus}
                  isOperational={isOperational}
                  hasResults={Boolean(pageItems?.length)}
                  isLoading={isLoading}
                />
                <Overview
                  limit={limit}
                  isLoading={isLoading}
                  pageItems={pageItems ?? []}
                  totalAmount={totalAmount}
                  amountOfPages={amountOfPages}
                  currentPage={currentPage}
                  detailWildcardPage={detailWildcardPage}
                  title={title}
                />
              </Box>
            </ScrollLinkWrapper>
          </GridItem>
        </PageGrid>
      </ScrollLinkWrapper>
    </>
  );
};
